import React, { useState } from "react";
import { Badge, Dropdown, Form, ListGroup } from "react-bootstrap";
import { Check, Plus } from "react-bootstrap-icons";

interface IProps<T> {
  label: string;
  items: T[];
  selected: string[];
  onToggle: (id: string) => void;
  onBulk?: (ids: string[], isAdd: boolean) => void;
  getId: (item: T) => string;
  getName: (item: T) => string;
  getSub?: (item: T) => string;
  variant?: "primary" | "success"; // 추가 칩 색상 톤 (Bootstrap 변형)
  color?: string;                   // 커스텀 배경색 (지정 시 variant 대신 사용)
  width?: number;
}

// 캐럿 없는 칩 형태의 토글
const ChipToggle = React.forwardRef<
  HTMLSpanElement,
  { onClick?: (e: React.MouseEvent) => void; children?: React.ReactNode; tone: string; color?: string }
>(({ onClick, children, tone, color }, ref) => (
  <Badge
    ref={ref}
    bg={color ? "" : tone}
    role="button"
    className="d-inline-flex align-items-center gap-1 fw-medium"
    style={{ cursor: "pointer", padding: "7px 11px", ...(color ? { backgroundColor: color } : {}) }}
    onClick={onClick}
  >
    <Plus size={13} />
    {children}
  </Badge>
));
ChipToggle.displayName = "ChipToggle";

function MultiPicker<T>({
  label, items, selected, onToggle, onBulk,
  getId, getName, getSub, variant = "primary", color, width = 320,
}: IProps<T>) {
  const [q, setQ] = useState("");
  const filtered = items.filter((it) => (getName(it) ?? "").toLowerCase().includes(q.toLowerCase()));
  const allSel = filtered.length > 0 && filtered.every((it) => selected.includes(getId(it)));

  return (
    <Dropdown autoClose="outside" onToggle={(next) => { if (!next) setQ(""); }}>
      <Dropdown.Toggle as={ChipToggle} tone={variant} color={color}>{label}</Dropdown.Toggle>
      {/* renderOnMount: popper 인스턴스를 첫 오픈 전에 생성 → 첫 클릭 시 좌상단 깜빡임 방지 */}
      <Dropdown.Menu renderOnMount style={{ width, padding: 0 }} popperConfig={{ strategy: "fixed" }}>
        <div className="p-2 border-bottom">
          <Form.Control
            autoFocus
            size="sm"
            placeholder="이름으로 검색…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <div className="d-flex align-items-center px-2 py-1 border-bottom">
          <small className="text-muted">{selected.length}개 선택됨</small>
          <span className="flex-grow-1" />
          {onBulk && filtered.length > 0 && (
            <Dropdown.Item
              as="button"
              className="text-primary small p-0 w-auto bg-transparent border-0"
              onClick={() => onBulk(filtered.map(getId), !allSel)}
            >
              {allSel ? "검색결과 해제" : `검색결과 전체 (${filtered.length})`}
            </Dropdown.Item>
          )}
        </div>
        <ListGroup variant="flush" style={{ maxHeight: 260, overflowY: "auto" }}>
          {filtered.length === 0 && (
            <ListGroup.Item className="text-muted small text-center py-3">검색 결과 없음</ListGroup.Item>
          )}
          {filtered.map((it) => {
            const id = getId(it);
            const on = selected.includes(id);
            return (
              <ListGroup.Item
                key={id}
                action
                onClick={() => onToggle(id)}
                className="d-flex align-items-center gap-2 py-2"
                style={{ cursor: "pointer" }}
              >
                <span
                  className="d-inline-flex align-items-center justify-content-center border rounded"
                  style={{ width: 16, height: 16, flexShrink: 0 }}
                >
                  {on && <Check size={12} />}
                </span>
                <span className="text-truncate">
                  {getName(it)}
                  {getSub && <span className="text-muted ms-1 small">{getSub(it)}</span>}
                </span>
              </ListGroup.Item>
            );
          })}
        </ListGroup>
      </Dropdown.Menu>
    </Dropdown>
  );
}

export default MultiPicker;
